/**
 * DaVinci API Return Codes
 *
 * Central registry for all BA (Booking Action) return codes.
 * This is the single source of truth for code meanings and behaviors.
 */

/**
 * Booking action types that can follow a BA check
 */
export type BookingActionType = 'B' | 'BQ' | 'BC';

/**
 * Booking flow categories for UI display
 */
export type BookingFlowType = 'fixed' | 'request' | 'confirmation';

/**
 * Alert/status type for UI display
 */
export type StatusType = 'info' | 'success' | 'warning' | 'error';

/**
 * Return code metadata
 */
export interface ReturnCodeMeta {
  /** The numeric return code */
  code: number;
  /** DaVinci description (for debugging/documentation) */
  description: string;
  /**
   * Booking action to execute (B, BQ, BC).
   * Optional - only set for codes that determine booking action (712, 733, 923, etc.).
   * Address validation codes (726, 727) don't have this as they don't determine the action.
   */
  action?: BookingActionType;
  /**
   * UI flow category.
   * Optional - only set for codes that determine booking flow.
   * Address validation codes (726, 727) don't have this as they don't determine the flow.
   */
  flowType?: BookingFlowType;
  /** Whether this code indicates availability (can proceed to checkout) */
  isAvailable: boolean;
  /** Translation key for user-facing message */
  translationKey: string;
  /** Fallback message if translation key is not found */
  fallbackMessage: string;
  /** Status type for UI alerts (info, success, warning, error) */
  statusType: StatusType;
}

/**
 * BA Action Return Codes Registry
 *
 * All codes that indicate successful availability check.
 * Codes not in this registry are considered errors/unavailable.
 */
export const BA_RETURN_CODES: Record<number, ReturnCodeMeta> = {
  // Fixed booking codes (immediate booking via B action)
  712: {
    code: 712,
    description: 'Fixed booking available',
    action: 'B',
    flowType: 'fixed',
    isAvailable: true,
    translationKey: 'ibe.availability.immediate',
    fallbackMessage: 'Available for immediate booking',
    statusType: 'success',
  },

  // Request booking codes (booking query via BQ action)
  707: {
    code: 707,
    description: 'Allotment released, use action BQ',
    action: 'BQ',
    flowType: 'request',
    isAvailable: true,
    translationKey: 'ibe.availability.on-request',
    fallbackMessage: 'Available on request',
    statusType: 'info',
  },
  733: {
    code: 733,
    description: 'Request booking available',
    action: 'BQ',
    flowType: 'request',
    isAvailable: true,
    translationKey: 'ibe.availability.on-request',
    fallbackMessage: 'Available on request',
    statusType: 'info',
  },
  734: {
    code: 734,
    description: 'Booking possible on request, please complete',
    action: 'BQ',
    flowType: 'request',
    isAvailable: true,
    translationKey: 'ibe.availability.on-request',
    fallbackMessage: 'Available on request',
    statusType: 'info',
  },

  // Confirmation codes (confirmation via BC action)
  923: {
    code: 923,
    description: 'Booking needs confirmation',
    action: 'BC',
    flowType: 'confirmation',
    isAvailable: true,
    translationKey: 'ibe.availability.confirmation',
    fallbackMessage: 'Booking requires confirmation',
    statusType: 'warning',
  },

  // Address validation codes (recoverable - require form update)
  726: {
    code: 726,
    description: 'Address corrected, please verify',
    isAvailable: false,
    translationKey: 'ibe.checkout.address.corrected',
    fallbackMessage: 'Your address has been updated. Please verify the changes.',
    statusType: 'warning',
  },
  727: {
    code: 727,
    description: 'Address issue, please verify',
    isAvailable: false,
    translationKey: 'ibe.checkout.address.issue',
    fallbackMessage: 'There was an issue with your address. Please verify.',
    statusType: 'warning',
  },

  // Age/occupancy/insurance classification errors (non-bookable — for monitoring +
  // error display). The band-probe handles its own 670 internally and never surfaces it.
  // See claude/specs/ChildVsInfant/IMPLEMENTATION-SPEC.md §8.
  670: {
    code: 670,
    description: 'Alterseintrag ist falsch oder fehlt',
    isAvailable: false,
    translationKey: 'ibe.checkout.error.wrong-age-classification',
    fallbackMessage: 'The age or passenger type does not match the offer.',
    statusType: 'error',
  },
  332: {
    code: 332,
    description: 'Minimale Belegung unterschritten',
    isAvailable: false,
    translationKey: 'ibe.checkout.error.minimum-occupancy',
    fallbackMessage: 'The minimum occupancy for this room is not met.',
    statusType: 'error',
  },
  933: {
    code: 933,
    description: 'Anzahl Kinder größer als zulässig',
    isAvailable: false,
    translationKey: 'ibe.checkout.error.insurance-child-cap',
    fallbackMessage: 'The number of children exceeds what is allowed.',
    statusType: 'error',
  },
} as const;

/**
 * Success codes for booking submission (B, BQ, BC actions).
 * Single source of truth for success code definitions per action.
 *
 * BC accepts both 200 and 206: when BC confirms a booking whose underlying
 * type is "request" (e.g. originated from a 733 → 923 chain), the API returns
 * 206 + BookingNumber instead of 200.
 */
export const BOOKING_SUCCESS_CODES: {
  readonly FIXED_BOOKING: readonly number[];
  readonly REQUEST_BOOKING: readonly number[];
  readonly CONFIRMATION: readonly number[];
} = {
  /** B action (fixed booking) success */
  FIXED_BOOKING: [200],
  /** BQ action (request booking) success */
  REQUEST_BOOKING: [206],
  /** BC action (confirmation) success — fixed (200) or request (206) outcome */
  CONFIRMATION: [200, 206],
};

// ============================================
// Helper Functions (derived from registry)
// ============================================

/**
 * Get metadata for a return code
 *
 * @example
 * getCodeMeta(712) // { code: 712, action: 'B', flowType: 'fixed', ... }
 * getCodeMeta(999) // undefined
 */
export function getCodeMeta(code: number): ReturnCodeMeta | undefined {
  return BA_RETURN_CODES[code];
}

/**
 * Check if code indicates availability (any bookable state)
 *
 * @example
 * isAvailableCode(712) // true (fixed booking)
 * isAvailableCode(707) // true (request booking)
 * isAvailableCode(733) // true (request booking)
 * isAvailableCode(734) // true (request booking)
 * isAvailableCode(923) // true (confirmation)
 * isAvailableCode(999) // false (unknown)
 */
export function isAvailableCode(code: number): boolean {
  return BA_RETURN_CODES[code]?.isAvailable ?? false;
}

/**
 * Check if code requires fixed booking (B action)
 *
 * @example
 * isFixedBooking(712) // true
 * isFixedBooking(733) // false
 */
export function isFixedBooking(code: number): boolean {
  return BA_RETURN_CODES[code]?.action === 'B';
}

/**
 * Check if code requires request booking (BQ action)
 *
 * @example
 * isRequestBooking(707) // true
 * isRequestBooking(733) // true
 * isRequestBooking(734) // true
 * isRequestBooking(712) // false
 */
export function isRequestBooking(code: number): boolean {
  return BA_RETURN_CODES[code]?.action === 'BQ';
}

/**
 * Check if code requires confirmation (BC action)
 *
 * @example
 * isConfirmationBooking(923) // true
 * isConfirmationBooking(712) // false
 */
export function isConfirmationBooking(code: number): boolean {
  return BA_RETURN_CODES[code]?.action === 'BC';
}

/**
 * Get booking action type for a code
 *
 * @example
 * getActionForCode(712) // 'B'
 * getActionForCode(733) // 'BQ'
 * getActionForCode(923) // 'BC'
 * getActionForCode(999) // undefined
 */
export function getActionForCode(code: number): BookingActionType | undefined {
  return BA_RETURN_CODES[code]?.action;
}

/**
 * Get booking flow type for UI display
 *
 * @example
 * getFlowType(712) // 'fixed'
 * getFlowType(733) // 'request'
 * getFlowType(923) // 'confirmation'
 * getFlowType(999) // undefined
 */
export function getFlowType(code: number): BookingFlowType | undefined {
  return BA_RETURN_CODES[code]?.flowType;
}

/**
 * Get all available codes as array (for validation)
 *
 * @example
 * getAvailableCodes() // [712, 707, 733, 734, 923]
 */
export function getAvailableCodes(): number[] {
  return Object.values(BA_RETURN_CODES)
    .filter((meta) => meta.isAvailable)
    .map((meta) => meta.code);
}

/**
 * Get translation key for a code
 * Returns the translation key that should be passed to $t() for translation.
 *
 * @example
 * getTranslationKeyForCode(712) // 'ibe.availability.immediate'
 * getTranslationKeyForCode(733) // 'ibe.availability.on-request'
 * getTranslationKeyForCode(999) // 'ibe.availability.unavailable'
 */
export function getTranslationKeyForCode(code: number): string {
  const meta = BA_RETURN_CODES[code];
  if (meta) {
    return meta.translationKey;
  }
  return 'ibe.availability.unavailable';
}

/**
 * Get status type for UI alerts
 * Returns the appropriate alert/status type for a given code.
 *
 * @example
 * getStatusTypeForCode(712) // 'success'
 * getStatusTypeForCode(923) // 'warning'
 * getStatusTypeForCode(999) // 'error'
 */
export function getStatusTypeForCode(code: number): StatusType {
  const meta = BA_RETURN_CODES[code];
  if (meta) {
    return meta.statusType;
  }
  return 'error';
}

/**
 * Get translated message for a code with proper fallback
 * Combines translation key lookup with fallback message.
 *
 * @example
 * getMessageForCode(712) // "Available for immediate booking" (or translated)
 * getMessageForCode(733) // "Available on request" (or translated)
 * getMessageForCode(999) // "This offer is not available"
 */
export function getMessageForCode(
  code: number,
  $t: (key: string, fallback: string) => string
): string {
  const meta = BA_RETURN_CODES[code];
  if (meta) {
    return $t(meta.translationKey, meta.fallbackMessage);
  }
  return $t('ibe.availability.unavailable', 'This offer is not available');
}

// ============================================
// Address Validation Helper Functions
// ============================================

/**
 * Check if code ALWAYS indicates address validation issue (726, 727)
 * Note: 734 may or may not have address issues - check data instead
 *
 * @example
 * isAddressValidationCode(726) // true
 * isAddressValidationCode(727) // true
 * isAddressValidationCode(734) // false (734 may or may not have address issues)
 */
export function isAddressValidationCode(code: number): boolean {
  return code === 726 || code === 727;
}

/**
 * Check if code requires switching to request booking flow
 *
 * @example
 * requiresRequestBookingSwitch(734) // true
 * requiresRequestBookingSwitch(712) // false
 */
export function requiresRequestBookingSwitch(code: number): boolean {
  return code === 734;
}
